import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import clsx from "clsx";
import { createPluggable } from "pluggable-react";
import styles from "./Progress.module.scss";
import { withRefProp } from "../../../utils/withRefProp";

export type ProgressSize = "sm" | "md" | "lg";
export type ProgressIntent = "primary" | "secondary" | "success" | "warning" | "destructive";

export interface ProgressProps extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  ref?: React.ComponentPropsWithRef<typeof ProgressPrimitive.Root>["ref"];
  value?: number | null;
  max?: number;
  size?: ProgressSize;
  intent?: ProgressIntent;
  indicatorClassName?: string;
}

const clampProgress = (value: number | null | undefined, max: number) => {
  if (value === null || value === undefined) return null;
  return Math.min(max, Math.max(0, value));
};

const DefaultProgress = withRefProp(
  ({
    className,
    indicatorClassName,
    value,
    max = 100,
    size = "md",
    intent = "primary",
    ref,
    ...props
  }: ProgressProps) => {
    const resolvedValue = clampProgress(value, max);
    const percentage = resolvedValue === null || max <= 0 ? 0 : (resolvedValue / max) * 100;

    return (
      <ProgressPrimitive.Root
        ref={ref}
        className={clsx(styles.progress, className)}
        data-size={size}
        data-intent={intent}
        data-indeterminate={resolvedValue === null ? "true" : "false"}
        value={resolvedValue}
        max={max}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={clsx(styles.indicator, indicatorClassName)}
          style={{ transform: `translateX(-${100 - percentage}%)` }}
        />
      </ProgressPrimitive.Root>
    );
  },
);

const Progress = createPluggable<ProgressProps>("deci.progress", DefaultProgress);

export default Progress;
